import Button from "@/v2-backoffice-client/components/button"
import {
  LEGAL_BILLING_MARK_LABEL,
  LEGAL_BILLING_UNMARK_LABEL,
} from "@/v2-backoffice-client/legalBilling/legalBilling.constants"
import {
  invoicedPurchaseIds,
  uninvoicedPurchaseIds,
} from "@/v2-backoffice-client/legalBilling/legalBilling.utils"
import type {
  LegalBillingBatch,
  LegalBillingInvoiceAction,
} from "@/v2-backoffice-client/legalBilling/legalBilling.types"

export type LegalBillingInvoiceActionsProps = {
  /** The batches CURRENTLY VISIBLE under this group — see the ids note below. */
  batches: LegalBillingBatch[]
  isPending?: boolean
  onInvoice(action: LegalBillingInvoiceAction): void
  /** Names the group in the button's accessible label ("Mark ACME invoiced"). */
  scopeLabel: string
}

// Mark / unmark for one group. Both buttons are shown only when the group actually has
// rows in both states, so a half-invoiced group is honest about it rather than presenting
// one action that silently covers part of the group.
//
// The ids come from what is ON SCREEN, not from the group's server-resolved PurchaseIds:
// with the hide-invoiced toggle on, the marked rows are not visible, and acting on rows an
// admin cannot see is precisely the mistaken-mark scenario the reversible design exists to
// absorb. With invoiced rows hidden, Unmark therefore falls away entirely.
// The accessible label names the group and the row count, so a screen reader hears which
// of several same-named groups (two tenants can both use the tag "ACME") is about to be
// billed.
function rowsLabel(count: number): string {
  return `${count} ${count === 1 ? "row" : "rows"}`
}

function LegalBillingInvoiceActions(props: LegalBillingInvoiceActionsProps) {
  const { batches, isPending, onInvoice, scopeLabel } = props

  const toMark = uninvoicedPurchaseIds(batches)
  const toUnmark = invoicedPurchaseIds(batches)

  return (
    <div className="legal-billing-invoice-actions flex items-center gap-2">
      {toMark.length > 0 ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          isLoading={isPending}
          iconSrc="file-invoice"
          aria-label={`${LEGAL_BILLING_MARK_LABEL}: ${scopeLabel} (${rowsLabel(toMark.length)})`}
          data-feature-code="UI_BackofficeLegalBilling_Group_MarkInvoiced"
          onClick={() => onInvoice({ purchaseIds: toMark, isInvoiced: true })}
        >
          {LEGAL_BILLING_MARK_LABEL} ({toMark.length})
        </Button>
      ) : null}

      {toUnmark.length > 0 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          isLoading={isPending}
          iconSrc="undo"
          aria-label={`${LEGAL_BILLING_UNMARK_LABEL}: ${scopeLabel} (${rowsLabel(toUnmark.length)})`}
          data-feature-code="UI_BackofficeLegalBilling_Group_UnmarkInvoiced"
          onClick={() =>
            onInvoice({ purchaseIds: toUnmark, isInvoiced: false })
          }
        >
          {LEGAL_BILLING_UNMARK_LABEL} ({toUnmark.length})
        </Button>
      ) : null}
    </div>
  )
}

export default LegalBillingInvoiceActions
